import { Controller } from '@hotwired/stimulus'
import { get, post, put } from '@rails/request.js'
import { TelnyxRTC } from '@telnyx/webrtc'
import { Timer } from 'easytimer.js'
import VoicemailDrop from '../voicemail_drop'

export default class extends Controller {
  static targets = [
    'contactCard', 'outcomePanel', 'countdown', 'rescheduleField',
    'rescheduleInput', 'notes', 'submitButton', 'pauseButton', 'error',
    'callStatus', 'dialButton', 'skipButton', 'muteButton', 'hangupButton',
    'outcomeButton', 'incomingToast', 'voicemailControls', 'defaultVoicemailButton'
  ]

  static values = {
    id: Number,
    wrapUpSeconds: Number,
    autoDial: Boolean,
    telnyxToken: String,
    callerNumber: String,
    recordCalls: Boolean,
    sipUsername: String,
    sipPassword: String,
    organizationId: String,
    authorizeUrl: String,
    voicemailUrl: String,
    myVoicemailUrl: String
  }

  initialize() {
    this.resetSessionState()
  }

  connect() {
    this.resetSessionState()
    this.ringingTone = new Audio('/dtmf/phone-ringing.mp3')
    this.connectTelnyxClient()
  }

  resetSessionState() {
    this.paused = true
    this.awaitingOutcome = false
    this.autoAdvancing = false
    this.submitting = false
    this.inCall = false
    this.callConnected = false
    this.isMuted = false
    this.clientReady = false
    this.pendingDial = false
    this.selectedOutcomeId = null
    this.selectedOutcomeKind = null
    this.currentCall = null
    this.callLog = null
    this.maxCallSeconds = null
    this.discardNextHangup = false
    this.lastCallConnected = false
    this.incomingToastCall = null
    this.voicemailDrops = new Map()
    this.voicemailCallIds = new Set()
    this.finishForm = null
  }

  disconnect() {
    this.voicemailDrops.forEach((drop) => drop.hangup())
    if (this.currentCall && this.currentCall.state !== 'hangup' && this.currentCall.state !== 'destroy') {
      this.currentCall.hangup()
    }
    if (this.telnyxClient) {
      this.telnyxClient.disconnect()
      this.telnyxClient.off('telnyx.ready')
      this.telnyxClient.off('telnyx.error')
      this.telnyxClient.off('telnyx.notification')
    }
    this.stopCountdown()
    this.stopCallTimer()
    this.ringingTone.pause()
    this.audioElement?.remove()
  }

  connectTelnyxClient() {
    const telnyxParams = {
      audio: true,
      video: false,
      register: true,
      ...this.telnyxLoginParams()
    }

    if (!telnyxParams.login_token && !telnyxParams.login) {
      this.setStatus('Connection error — reload the page')
      return
    }

    this.telnyxClient = new TelnyxRTC(telnyxParams)

    this.telnyxClient.on('telnyx.ready', () => {
      this.clientReady = true
      this.tokenRefreshAttempted = false
      if (this.pendingDial) this.dialCurrent()
    })

    this.telnyxClient.on('telnyx.error', (err) => {
      this.setStatus('Connection error')
      if (err?.error?.name === 'LOGIN_FAILED') this.reconnectWithFreshToken()
    })

    this.telnyxClient.on('telnyx.notification', (notification) => {
      const call = notification.call
      if (!call) return

      if (this.voicemailCallIds.has(call.id)) {
        const drop = this.voicemailDrops.get(call.id)
        if (drop && notification.type === 'callUpdate') drop.handleUpdate(call)
        return
      }

      if (call.direction === 'inbound' && call !== this.currentCall) {
        if (notification.type === 'callUpdate') this.handleIncomingCallUpdate(call)
        return
      }

      if (call.state === 'active' && call.remoteStream) {
        this.ensureAudioElement()
        this.audioElement.srcObject = call.remoteStream
        this.audioElement.play().catch(() => {})
      }

      if (notification.type !== 'callUpdate') return

      switch (call.state) {
        case 'new':
        case 'requesting':
          this.ringingTone.play().catch(() => {})
          this.setStatus('Dialing…')
          break
        case 'trying':
        case 'early':
          this.setStatus('Ringing…')
          break
        case 'active':
          this.callConnected = true
          this.ringingTone.pause()
          this.startCallTimer()
          this.updateControls()
          break
        case 'hangup':
          this.handleHangup()
          break
        case 'destroy':
          this.ringingTone.pause()
          if (this.inCall) this.handleHangup()
          this.currentCall = null
          break
      }
    })

    this.telnyxClient.connect()
  }

  contactCardTargetConnected() {
    this.updateControls()
    if (this.countdownTimer && this.hasCountdownTarget) this.countdownTarget.classList.remove('hidden')
    if (this.autoAdvancing) {
      this.autoAdvancing = false
      this.dialCurrent()
    }
  }

  dialCurrent() {
    if (this.paused || this.inCall || this.awaitingOutcome) return
    if (!this.hasContactCardTarget) return

    if (!this.telnyxClient) {
      this.setStatus('Connection error — reload the page')
      return
    }

    if (!this.clientReady) {
      this.pendingDial = true
      this.setStatus('Connecting…')
      return
    }

    this.pendingDial = false
    this.authorizeAndDial()
  }

  async authorizeAndDial() {
    this.inCall = true
    this.updateControls()
    this.setStatus('Authorizing…')

    const card = this.contactCardTarget.dataset
    const origin = this.callerNumberValue
    const separator = this.authorizeUrlValue.includes('?') ? '&' : '?'
    const url = `${this.authorizeUrlValue}${separator}record_call=${this.recordCallsValue}` +
      `&country=${encodeURIComponent(card.countryCode || '')}` +
      `&origin=${encodeURIComponent(origin)}` +
      `&destination=${encodeURIComponent(card.phoneNumber)}` +
      `&dial_session_id=${this.idValue}`

    const response = await get(url, { responseKind: 'json' })
    const body = response.ok ? await response.json : {}

    if (!body.success) {
      this.inCall = false
      this.updateControls()
      this.setStatus(this.authorizationFailureLabel(body.reason))
      return
    }

    this.maxCallSeconds = body.max_call_seconds
    this.startCall(card, origin)
  }

  authorizationFailureLabel(reason) {
    if (reason === 'credits') return 'Insufficient credits'
    if (reason === 'call_in_progress') return 'A call is already in progress'
    return 'Call not authorized'
  }

  startCall(card, origin) {
    this.callConnected = false
    this.isMuted = false
    this.setStatus('Dialing…')

    this.currentCall = this.telnyxClient.newCall({
      destinationNumber: card.phoneNumber,
      callerNumber: origin
    })

    this.createCallLog(card)
  }

  async createCallLog(card) {
    const response = await post('/call_logs', {
      body: {
        phone_number: card.phoneNumber,
        telnyx_id: this.currentCall.id,
        direction: 'outbound',
        organization_id: this.organizationIdValue,
        contact_id: card.contactId,
        dial_session_id: this.idValue
      },
      responseKind: 'json'
    })

    if (response.ok) {
      const body = await response.json
      this.callLog = body['data']
      if (!this.inCall) this.finishCallLog()
      this.updateControls()
    } else if (this.currentCall) {
      this.currentCall.hangup()
    }
  }

  async handleHangup() {
    this.ringingTone.pause()
    this.stopCallTimer()

    const connected = this.callConnected
    this.lastCallConnected = connected
    this.inCall = false
    this.callConnected = false
    this.setStatus('Call ended')
    const hangupRecorded = this.finishCallLog()

    if (this.discardNextHangup) {
      this.discardNextHangup = false
      this.updateControls()
      if (this.finishForm) this.setStatus('Finishing after voicemails…')
      return
    }

    this.awaitingOutcome = true
    this.selectedOutcomeId = null
    this.selectedOutcomeKind = null
    if (this.hasOutcomePanelTarget) this.outcomePanelTarget.classList.remove('hidden')
    this.updateControls()

    if (!connected && this.autoDialValue) {
      await hangupRecorded
      this.startCountdown()
    }
  }

  async finishCallLog() {
    if (!this.callLog) return

    const callLogId = this.callLog.id
    this.callLog = null
    await put(`/call_logs/${callLogId}/hangup`, { responseKind: 'json' })
  }

  hangup() {
    if (this.currentCall && this.currentCall.state !== 'hangup' && this.currentCall.state !== 'destroy') {
      this.currentCall.hangup()
    }
  }

  pressDigit(event) {
    const digit = event.currentTarget.dataset.digit
    this.playDigitTone(digit)
    if (this.currentCall && this.currentCall.state === 'active') this.currentCall.dtmf(digit)
  }

  playDigitTone(digit) {
    const name = digit === '*' ? 'star' : (digit === '#' ? 'pound' : digit)
    new Audio(`/dtmf/dtmf-${name}.mp3`).play().catch(() => {})
  }

  toggleMute() {
    if (!this.currentCall) return

    if (this.isMuted) {
      this.currentCall.unmuteAudio()
      this.isMuted = false
    } else {
      this.currentCall.muteAudio()
      this.isMuted = true
    }
    if (this.hasMuteButtonTarget) this.muteButtonTarget.textContent = this.isMuted ? 'Unmute' : 'Mute'
  }

  updateControls() {
    if (this.hasDialButtonTarget) this.dialButtonTarget.classList.toggle('hidden', this.inCall || this.awaitingOutcome)
    if (this.hasSkipButtonTarget) this.skipButtonTarget.classList.toggle('hidden', this.inCall)
    if (this.hasHangupButtonTarget) this.hangupButtonTarget.classList.toggle('hidden', !this.inCall)
    if (this.hasMuteButtonTarget) {
      this.muteButtonTarget.classList.toggle('hidden', !(this.inCall && this.callConnected))
      if (!this.isMuted) this.muteButtonTarget.textContent = 'Mute'
    }
    if (this.hasVoicemailControlsTarget) {
      this.voicemailControlsTarget.classList.toggle('hidden', !(this.inCall && this.callConnected && this.callLog))
    }
  }

  async leaveVoicemail({ params: { url } }) {
    if (!url || !this.currentCall || !this.callConnected || !this.callLog || this.submitting) return
    this.submitting = true

    const call = this.currentCall
    const callLogId = this.callLog.id
    const elapsed = this.callTimer ? this.callTimer.getTotalTimeValues().seconds : 0

    this.stopCallTimer()
    if (this.audioElement) this.audioElement.srcObject = null
    this.currentCall = null
    this.callLog = null
    this.inCall = false
    this.callConnected = false
    this.updateControls()
    this.setStatus('Ready')

    const drop = new VoicemailDrop({
      call,
      audioUrl: url,
      maxSeconds: this.maxCallSeconds ? this.maxCallSeconds - elapsed : null,
      onFinish: () => this.voicemailFinished(call.id, callLogId)
    })
    this.voicemailCallIds.add(call.id)
    this.voicemailDrops.set(call.id, drop)
    drop.start().catch(() => drop.hangup())

    this.autoAdvancing = this.autoDialValue && !this.paused
    try {
      const response = await put(this.voicemailUrlValue, { responseKind: 'turbo-stream' })
      if (!response.ok) {
        this.autoAdvancing = false
        this.setStatus('Could not save the voicemail outcome')
      }
    } finally {
      this.submitting = false
    }
  }

  async voicemailFinished(callId, callLogId) {
    this.voicemailDrops.delete(callId)
    try {
      await put(`/call_logs/${callLogId}/hangup`, { responseKind: 'json' })
    } finally {
      const finishForm = this.finishForm
      if (finishForm && this.voicemailDrops.size === 0) {
        this.finishForm = null
        finishForm.requestSubmit()
      }
    }
  }

  async chooseMyVoicemail(event) {
    const option = event.currentTarget.selectedOptions[0]
    if (option.dataset.url) {
      this.defaultVoicemailButtonTarget.dataset.dialSessionUrlParam = option.dataset.url
    } else {
      delete this.defaultVoicemailButtonTarget.dataset.dialSessionUrlParam
    }
    this.defaultVoicemailButtonTarget.disabled = !option.dataset.url

    await put(this.myVoicemailUrlValue, { body: { voicemail_id: event.currentTarget.value }, responseKind: 'json' })
  }

  finishSession(event) {
    if (this.voicemailDrops.size === 0) return
    event.preventDefault()
    this.finishForm = event.currentTarget
    this.setPaused(true)
    if (this.inCall) this.discardNextHangup = true
    this.hangup()
    this.setStatus('Finishing after voicemails…')
  }

  setStatus(text) {
    this.callStatusTargets.forEach((target) => { target.textContent = text })
  }

  ensureAudioElement() {
    if (this.audioElement) return

    this.audioElement = new Audio()
    this.audioElement.autoplay = true
    this.audioElement.hidden = true
    document.body.appendChild(this.audioElement)
  }

  startCallTimer() {
    this.stopCallTimer()
    this.callTimer = new Timer()
    this.callTimer.addEventListener('secondsUpdated', () => {
      const elapsed = this.callTimer.getTimeValues()
      this.setStatus(`Connected ${elapsed.toString(['minutes', 'seconds'])}`)
      const totalSeconds = elapsed.seconds + elapsed.minutes * 60 + elapsed.hours * 3600
      if (this.maxCallSeconds && totalSeconds >= this.maxCallSeconds) {
        this.hangup()
      }
    })
    this.callTimer.start()
  }

  stopCallTimer() {
    if (this.callTimer) {
      this.callTimer.stop()
      this.callTimer = null
    }
  }

  async reconnectWithFreshToken() {
    if (this.sipUsernameValue || this.tokenRefreshAttempted) return
    this.tokenRefreshAttempted = true

    const response = await put('/telnyx_token', {
      body: { kind: this.recordCallsValue ? 'record' : 'regular' },
      responseKind: 'json'
    })
    if (!response.ok) return

    const { token } = await response.json
    if (!token) return

    this.telnyxTokenValue = token
    this.clientReady = false
    this.telnyxClient.disconnect()
    this.telnyxClient.off('telnyx.ready')
    this.telnyxClient.off('telnyx.error')
    this.telnyxClient.off('telnyx.notification')
    this.connectTelnyxClient()
  }

  telnyxLoginParams() {
    if (this.sipUsernameValue) return { login: this.sipUsernameValue, password: this.sipPasswordValue }
    return { login_token: this.telnyxTokenValue }
  }

  handleIncomingCallUpdate(call) {
    if (!this.hasIncomingToastTarget) return

    if (call.state === 'ringing') {
      this.incomingToastCall = call
      this.incomingToastTarget.textContent = `Incoming call on ${call.options.callerNumber} from ${call.options.remoteCallerNumber}`
      this.incomingToastTarget.classList.remove('hidden')
      this.lookupIncomingCaller(call)
      return
    }

    if (call === this.incomingToastCall && (call.state === 'hangup' || call.state === 'destroy')) {
      this.incomingToastCall = null
      this.incomingToastTarget.classList.add('hidden')
    }
  }

  async lookupIncomingCaller(call) {
    const response = await get(`/contacts/lookup?phone_number=${encodeURIComponent(call.options.remoteCallerNumber)}`, { responseKind: 'json' })
    if (!response.ok || this.incomingToastCall !== call) return

    const body = await response.json
    if (body.name) this.incomingToastTarget.textContent = `Incoming call on ${call.options.callerNumber} from ${body.name}`
  }

  pickOutcome(event) {
    this.clearError()
    this.stopCountdown()
    this.selectedOutcomeId = event.currentTarget.dataset.outcomeId
    this.selectedOutcomeKind = event.currentTarget.dataset.outcomeKind
    this.outcomeButtonTargets.forEach((button) => {
      const selected = button === event.currentTarget
      button.classList.toggle('ring-2', selected)
      button.classList.toggle('ring-offset-2', selected)
      button.classList.toggle('ring-primary', selected)
    })
    this.rescheduleFieldTarget.classList.toggle('hidden', this.selectedOutcomeKind !== 'reschedule')
  }

  async submitOutcome() {
    if (this.submitting) return
    this.submitting = true

    this.stopCountdown()
    this.awaitingOutcome = false

    const body = { notes: this.notesTarget.value }
    if (this.selectedOutcomeId) body.outcome_id = this.selectedOutcomeId
    if (this.selectedOutcomeKind === 'reschedule') body.next_call_at = this.rescheduleInputTarget.value

    try {
      const response = await put(`/dial_sessions/${this.idValue}/outcome`, {
        body,
        responseKind: 'turbo-stream'
      })

      if (response.ok) {
        this.clearError()
        this.afterAdvance()
      } else {
        this.autoAdvancing = false
        this.awaitingOutcome = true
        const data = await response.json.catch(() => ({}))
        this.showError(this.outcomeErrorMessage(data.error))
      }
    } finally {
      this.submitting = false
    }
  }

  async skip() {
    if (this.submitting) return
    this.submitting = true

    if (this.inCall) this.discardNextHangup = true
    this.hangup()
    this.stopCountdown()
    this.awaitingOutcome = false

    try {
      const response = await put(`/dial_sessions/${this.idValue}/skip`, { responseKind: 'turbo-stream' })
      if (response.ok) this.afterAdvance()
    } finally {
      this.submitting = false
    }
  }

  outcomeErrorMessage(errorKey) {
    if (errorKey === 'next_call_at_required') return 'Pick a callback time to continue'
    return 'Pick an outcome to continue'
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('hidden')
  }

  afterAdvance() {
    if (!this.autoAdvancing && this.autoDialValue) this.startCountdown()
  }

  dialNow() {
    if (this.awaitingOutcome) return

    this.stopCountdown()
    if (this.paused) {
      this.setPaused(false)
    } else {
      this.dialCurrent()
    }
  }

  startCountdown() {
    if (!this.hasCountdownTarget) return

    this.stopCountdown()
    this.countdownTarget.classList.remove('hidden')
    this.countdownTimer = new Timer()

    this.countdownTimer.addEventListener('secondsUpdated', () => {
      const remaining = this.countdownTimer.getTimeValues().toString(['minutes', 'seconds'])
      this.countdownTarget.classList.remove('hidden')
      this.countdownTarget.textContent = `Calling next in ${remaining}`
    })

    this.countdownTimer.addEventListener('targetAchieved', () => {
      this.stopCountdown()
      if (this.awaitingOutcome) {
        this.autoAdvancing = true
        this.submitOutcome()
      } else {
        this.dialCurrent()
      }
    })

    this.countdownTimer.start({
      countdown: true,
      startValues: { seconds: this.wrapUpSecondsValue }
    })
  }

  stopCountdown() {
    if (this.countdownTimer) {
      this.countdownTimer.stop()
      this.countdownTimer = null
    }
    if (this.hasCountdownTarget) this.countdownTarget.classList.add('hidden')
  }

  pause() {
    this.setPaused(!this.paused)
  }

  setPaused(paused) {
    this.paused = paused
    this.pauseButtonTarget.textContent = this.paused ? 'Resume' : 'Pause'
    this.pauseButtonTarget.classList.remove('bg-primary', 'text-primary-foreground', 'hover:bg-primary-hover')
    this.pauseButtonTarget.classList.add('border', 'border-border', 'hover:bg-raised')
    if (this.paused) {
      this.stopCountdown()
    } else if (this.awaitingOutcome) {
      if (!this.lastCallConnected) this.startCountdown()
    } else {
      this.dialCurrent()
    }
  }
}
